import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/utils-format";
import { StatusPill } from "@/lib/utils-format";
import {
  ArrowUpRight, Package, HourglassMedium, CheckCircle, Truck, CurrencyEur, Wallet, Coins,
  ChartLineUp, Gift, ArrowDown,
} from "@phosphor-icons/react";

function ProfitPanel({ mtd, allTime }) {
  if (!mtd) return null;
  const marginColor = (pct) => pct >= 40 ? "text-emerald-700" : pct >= 20 ? "text-amber-700" : pct >= 0 ? "text-zinc-700" : "text-red-700";

  return (
    <div className="border border-zinc-200 bg-white mb-6" data-testid="profit-panel">
      <div className="p-5 border-b border-zinc-200 flex items-center gap-2">
        <ChartLineUp size={16} weight="bold" className="text-emerald-700" />
        <h3 className="text-lg font-bold tracking-tight">Profitability</h3>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-auto">Based on verified paid orders only</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-zinc-200">
        <PeriodStats title="Month to date" data={mtd} marginColor={marginColor} testidPrefix="mtd" />
        <PeriodStats title="All time" data={allTime} marginColor={marginColor} testidPrefix="all-time" />
      </div>
    </div>
  );
}

function PeriodStats({ title, data, marginColor, testidPrefix }) {
  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{title}</p>
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono" data-testid={`${testidPrefix}-orders-count`}>{data.orders} orders</p>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <MetricSmall label="Revenue" value={`€${Number(data.revenue).toLocaleString()}`} icon={CurrencyEur} testid={`${testidPrefix}-revenue`} />
        <MetricSmall label="Cost" value={`€${Number(data.cost).toLocaleString()}`} icon={ArrowDown} testid={`${testidPrefix}-cost`} />
        <MetricSmall label="Profit" value={`€${Number(data.profit).toLocaleString()}`} icon={ChartLineUp} color={data.profit >= 0 ? "text-emerald-700" : "text-red-700"} testid={`${testidPrefix}-profit`} />
        <MetricSmall label="Margin" value={`${data.margin_pct}%`} color={marginColor(data.margin_pct)} testid={`${testidPrefix}-margin`} />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid={`${testidPrefix}-avg-profit`}>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Avg profit / order</p>
          <p className="text-base font-black tracking-tighter font-mono">€{Number(data.avg_profit_per_order).toLocaleString()}</p>
        </div>
        <div className="border border-emerald-200 bg-emerald-50/50 px-3 py-2" data-testid={`${testidPrefix}-promo-cost`}>
          <div className="flex items-center gap-1">
            <Gift size={11} weight="bold" className="text-emerald-700" />
            <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Free bags · promo cost</p>
          </div>
          <p className="text-base font-black tracking-tighter font-mono text-emerald-800">{data.free_bags} bags · €{Number(data.promo_cost).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

function MetricSmall({ label, value, icon: Icon, color = "", testid }) {
  return (
    <div className="border border-zinc-200 bg-white px-3 py-2" data-testid={testid}>
      <div className="flex items-center gap-1 mb-1">
        {Icon && <Icon size={11} weight="bold" className="text-zinc-400" />}
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</p>
      </div>
      <p className={`text-lg font-black tracking-tighter font-mono ${color}`}>{value}</p>
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div className="border border-zinc-200 p-5 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</p>
        {Icon && <Icon size={16} className={accent || "text-zinc-400"} weight="bold" />}
      </div>
      <p className="text-3xl font-bold tracking-tighter mb-1">{value}</p>
      {sub && <p className="text-xs text-zinc-500">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading dashboard…</div>;
  if (!data) return <div className="p-8 text-sm text-zinc-500">Failed to load dashboard.</div>;

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Overview</p>
        <h1 className="text-4xl font-black tracking-tighter">Operations Dashboard</h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI testid="kpi-today-orders" label="Today's Orders" value={data.today_orders_count} icon={Package} accent="text-zinc-900" />
        <KPI testid="kpi-waiting-payment" label="Waiting Payment" value={data.waiting_payment_count} icon={HourglassMedium} accent="text-amber-600" />
        <KPI testid="kpi-under-review" label="Under Review" value={data.under_review_count} icon={HourglassMedium} accent="text-blue-600" />
        <KPI testid="kpi-ready-to-ship" label="Ready to Ship" value={data.ready_to_ship_count} icon={CheckCircle} accent="text-emerald-600" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPI testid="kpi-shipped-today" label="Shipped Today" value={data.shipped_today_count} icon={Truck} accent="text-violet-600" />
        <KPI testid="kpi-today-revenue" label="Today's Revenue" value={formatCurrency(data.today_revenue, "EUR")} icon={CurrencyEur} accent="text-emerald-600" />
        <KPI testid="kpi-pending-revenue" label="Pending Revenue" value={formatCurrency(data.pending_revenue, "EUR")} icon={Coins} accent="text-amber-600" />
        <KPI testid="kpi-pending-collection" label="Pending Collection (₹)" value={`₹${Number(data.total_pending_collection).toLocaleString()}`} icon={Wallet} accent="text-indigo-600" />
      </div>

      {/* Profitability */}
      <ProfitPanel mtd={data.profit_mtd} allTime={data.profit_all_time} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Orders */}
        <div className="lg:col-span-2 border border-zinc-200 bg-white">
          <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="text-lg font-bold tracking-tight">Recent Orders</h3>
            <Link to="/orders" className="text-xs uppercase tracking-widest text-zinc-600 hover:text-zinc-900 flex items-center gap-1">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          <table className="w-full data-table">
            <thead>
              <tr>
                <th className="text-left px-5 py-2">Order #</th>
                <th className="text-left px-5 py-2">Customer</th>
                <th className="text-left px-5 py-2">Amount</th>
                <th className="text-left px-5 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_orders?.map((o) => (
                <tr key={o.order_id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`recent-order-${o.order_number}`}>
                  <td className="px-5 py-3 font-mono text-sm">
                    <Link to={`/orders/${o.order_id}`} className="hover:underline">{o.order_number}</Link>
                  </td>
                  <td className="px-5 py-3 text-sm">{o.customer_name}</td>
                  <td className="px-5 py-3 text-sm font-mono">{formatCurrency(o.total_amount, o.currency)}</td>
                  <td className="px-5 py-3"><StatusPill status={o.status} /></td>
                </tr>
              ))}
              {(!data.recent_orders || data.recent_orders.length === 0) && (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-zinc-500">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Account Balances */}
        <div className="border border-zinc-200 bg-white">
          <div className="p-5 border-b border-zinc-200">
            <h3 className="text-lg font-bold tracking-tight">Account Balances</h3>
            <p className="text-xs text-zinc-500 mt-1">Money in every account</p>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {data.account_balances?.map((acc) => (
              <div key={acc.account_id} className="px-5 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`balance-${acc.account_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <p className="text-sm font-medium">{acc.account_name}</p>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">{acc.owner || acc.country}</p>
                  </div>
                  <p className="text-sm font-mono font-semibold">
                    {acc.currency === "INR" ? "₹" : (acc.currency === "USD" ? "$" : (acc.currency === "GBP" ? "£" : "€"))}
                    {Number(acc.pending).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-4 text-[10px] uppercase tracking-widest text-zinc-500">
                  <span>Rcvd {Number(acc.received).toLocaleString()}</span>
                  <span>Coll {Number(acc.collected).toLocaleString()}</span>
                  <span>Txns {acc.transactions}</span>
                </div>
              </div>
            ))}
            {(!data.account_balances || data.account_balances.length === 0) && (
              <p className="p-5 text-sm text-zinc-500">No accounts with balance.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
